import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import {
    getAllCompaniesRequest,
    updateCompany as updateCompanyAction,
} from "../../redux/actions/companyactions";

import {
    getAllRolesRequest,
    setSelectedRole as setSelectedRoleAction, setSelectedRoleAdmin,
} from "../../redux/actions/roleActions";

import {
    getSidebarMenusByRoleRequest,
    createSidebarMenuRequest,
    updateSidebarMenuRequest,
    deleteSidebarMenuRequest, getSidebarMenusByRoleAdminRequest,
} from "../../redux/actions/sidebarMenuActions";

import "./SidebarMenuManagement.css";

/**
 * Normalize menus to a flat list:
 * - supports API returning either flat list OR roots with childMenus.
 */
const normalizeMenusToFlat = (menus) => {
    if (!Array.isArray(menus)) return [];

    const looksHierarchical = menus.some((m) => Array.isArray(m?.childMenus) && m.childMenus.length > 0);
    if (!looksHierarchical) return menus;

    const flat = [];
    const visit = (node, parentId = null) => {
        const n = {
            ...node,
            parentMenuId: node.parentMenuId ?? parentId,
            childMenus: undefined,
        };
        flat.push(n);

        (node.childMenus || []).forEach((child) => visit(child, n.id));
    };

    menus.forEach((root) => visit(root, null));
    return flat;
};

const buildTree = (flatMenus) => {
    const byParent = new Map();
    flatMenus.forEach((m) => {
        const pid = m.parentMenuId ?? null;
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(m);
    });

    // sort by displayOrder (fallback id)
    for (const [k, arr] of byParent.entries()) {
        arr.sort((a, b) => (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999) || (a.id ?? 0) - (b.id ?? 0));
        byParent.set(k, arr);
    }

    const roots = byParent.get(null) || [];
    return { roots, byParent };
};

const toMenuRequestDTO = (menu) => ({
    code: menu.code || "",
    name: menu.name || "",
    description: menu.description || "",
    roleId: menu.roleId ?? null,
    displayOrder: menu.displayOrder ?? 1,
    displayStatus: menu.displayStatus || "VISIBLE",
    icon: menu.icon || "",
    routePath: menu.routePath || "",
    parentMenuId: menu.parentMenuId ?? null,
});

/** ---------------- Modal ---------------- */
const SidebarMenuModal = ({ isOpen, onClose, onSubmit, editingMenu, loading, roles, rootMenus, selectedRoleId }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        code: "",
        name: "",
        description: "",
        roleId: selectedRoleId || "",
        displayOrder: 1,
        displayStatus: "VISIBLE",
        icon: "",
        routePath: "",
        parentMenuId: "",
    });

    useEffect(() => {
        if (!isOpen) return;

        if (editingMenu) {
            setFormData({
                code: editingMenu.code || "",
                name: editingMenu.name || "",
                description: editingMenu.description || "",
                roleId: editingMenu.roleId ?? selectedRoleId ?? "",
                displayOrder: editingMenu.displayOrder ?? 1,
                displayStatus: editingMenu.displayStatus || "VISIBLE",
                icon: editingMenu.icon || "",
                routePath: editingMenu.routePath || "",
                parentMenuId: editingMenu.parentMenuId ?? "",
            });
        } else {
            setFormData({
                code: "",
                name: "",
                description: "",
                roleId: selectedRoleId || "",
                displayOrder: 1,
                displayStatus: "VISIBLE",
                icon: "",
                routePath: "",
                parentMenuId: "",
            });
        }
    }, [editingMenu, isOpen, selectedRoleId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            ...formData,
            roleId: formData.roleId ? Number(formData.roleId) : null,
            displayOrder: formData.displayOrder ? Number(formData.displayOrder) : 1,
            parentMenuId: formData.parentMenuId ? Number(formData.parentMenuId) : null,
        };

        onSubmit(payload);
    };

    if (!isOpen) return null;

    return (
        <div className="sbm-modal-overlay" onClick={onClose}>
            <div className="sbm-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="sbm-modal-header">
                    <h2>{editingMenu ? "✏️ " + t("Modifier le menu") : "➕ " + t("Ajouter un menu")}</h2>
                    <button className="sbm-modal-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="sbm-modal-form">
                    <div className="sbm-form-row">
                        <div className="sbm-form-group">
                            <label>{t("Code")} *</label>
                            <input
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                required
                                maxLength={50}
                                placeholder={t("Ex: DASHBOARD")}
                                className="sbm-input"
                            />
                        </div>

                        <div className="sbm-form-group">
                            <label>{t("Nom")} *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                maxLength={200}
                                placeholder={t("Ex: Tableau de bord")}
                                className="sbm-input"
                            />
                        </div>
                    </div>

                    <div className="sbm-form-group">
                        <label>{t("Description")}</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder={t("Description du menu...")}
                            className="sbm-textarea"
                        />
                    </div>

                    <div className="sbm-form-row">
                        <div className="sbm-form-group">
                            <label>{t("Rôle")} *</label>
                            <select
                                name="roleId"
                                value={formData.roleId}
                                onChange={handleInputChange}
                                className="sbm-select"
                                required
                            >
                                <option value="">{t("Sélectionner...")}</option>
                                {(roles || []).map((r) => (
                                    <option key={r.id} value={r.id}>
                                        {r.name || r.roleName || `Role ${r.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="sbm-form-group">
                            <label>{t("Statut d'affichage")}</label>
                            <select
                                name="displayStatus"
                                value={formData.displayStatus}
                                onChange={handleInputChange}
                                className="sbm-select"
                            >
                                <option value="VISIBLE">{t("VISIBLE")}</option>
                                <option value="HIDDEN">{t("HIDDEN")}</option>
                            </select>
                        </div>
                    </div>

                    <div className="sbm-form-row">
                        <div className="sbm-form-group">
                            <label>{t("Icône")}</label>
                            <input
                                type="text"
                                name="icon"
                                value={formData.icon}
                                onChange={handleInputChange}
                                placeholder={t("Ex: dashboard")}
                                className="sbm-input"
                            />
                        </div>

                        <div className="sbm-form-group">
                            <label>{t("Route")}</label>
                            <input
                                type="text"
                                name="routePath"
                                value={formData.routePath}
                                onChange={handleInputChange}
                                placeholder={t("Ex: /dashboard")}
                                className="sbm-input"
                            />
                        </div>
                    </div>

                    <div className="sbm-form-row">
                        <div className="sbm-form-group">
                            <label>{t("Parent")}</label>
                            <select
                                name="parentMenuId"
                                value={formData.parentMenuId}
                                onChange={handleInputChange}
                                className="sbm-select"
                            >
                                <option value="">{t("Aucun (menu racine)")}</option>
                                {(rootMenus || []).map((m) => (
                                    <option key={m.id} value={m.id}>
                                        {m.name} ({m.code})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="sbm-form-group">
                            <label>{t("Ordre d'affichage")}</label>
                            <input
                                type="number"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                min={1}
                                className="sbm-input"
                            />
                            <small className="sbm-help">{t("Astuce: tu peux aussi réordonner par Drag & Drop.")}</small>
                        </div>
                    </div>

                    <div className="sbm-modal-actions">
                        <button type="button" className="sbm-btn sbm-btn-secondary" onClick={onClose} disabled={loading}>
                            {t("Annuler")}
                        </button>
                        <button type="submit" className="sbm-btn sbm-btn-primary" disabled={loading}>
                            {loading ? "⏳ " + t("Chargement...") : editingMenu ? "💾 " + t("Mettre à jour") : "➕ " + t("Créer")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/** -------------- Main Component -------------- */
const SidebarMenuManagement = ({
                                   companies,
                                   roles,
                                   menus,
                                   loading,
                                   error,

                                   // companies
                                   getAllCompanies,
                                   updateCompany,

                                   // roles
                                   getAllRoles,
                                   setSelectedRole,

                                   // menus
                                   getMenusByRole,
                                   createMenu,
                                   updateMenu,
                                   deleteMenu,
                               }) => {
    const { t } = useTranslation();

    const [selectedCompanyId, setSelectedCompanyId] = useState(sessionStorage.getItem("selectedCompanyId") || "");
    const [selectedRoleId, setSelectedRoleId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | VISIBLE | HIDDEN

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMenu, setEditingMenu] = useState(null);

    // Drag state
    const dragStateRef = useRef({
        draggedId: null,
        fromParentId: null,
    });

    useEffect(() => {
        getAllCompanies();
    }, []);

    // Normalize + build tree from reducer menus
    const flatMenus = useMemo(() => normalizeMenusToFlat(menus || []), [menus]);
    const { roots, byParent } = useMemo(() => buildTree(flatMenus), [flatMenus]);

    const rootMenus = useMemo(() => roots, [roots]);

    const filteredTree = useMemo(() => {
        const match = (m) => {
            const text = `${m.code || ""} ${m.name || ""} ${m.routePath || ""}`.toLowerCase();
            const okSearch = !searchTerm || text.includes(searchTerm.toLowerCase());
            const okStatus = statusFilter === "ALL" || (m.displayStatus || "").toUpperCase() === statusFilter;
            return okSearch && okStatus;
        };

        // filter roots and their children
        const filteredRoots = (roots || [])
            .map((r) => {
                const children = (byParent.get(r.id) || []).filter(match);
                const rootMatches = match(r);
                if (rootMatches || children.length > 0) return { root: r, children };
                return null;
            })
            .filter(Boolean);

        return filteredRoots;
    }, [roots, byParent, searchTerm, statusFilter]);

    const openCreateModal = (parentMenuId = null) => {
        setEditingMenu(null);

        // If parent is set, prefill parent in modal by setting editingMenu stub:
        if (parentMenuId) {
            setEditingMenu({
                parentMenuId,
                roleId: selectedRoleId ? Number(selectedRoleId) : null,
                displayStatus: "VISIBLE",
                displayOrder: 1,
            });
        } else {
            setEditingMenu(null);
        }

        setIsModalOpen(true);
    };

    const openEditModal = (menu) => {
        setEditingMenu(menu);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingMenu(null);
    };

    const onSubmitModal = (formData) => {
        if (!selectedCompanyId) {
            Swal.fire({ icon: "warning", title: "", text: t("Veuillez sélectionner une entreprise d'abord.") });
            return;
        }
        if (!formData.roleId) {
            Swal.fire({ icon: "warning", title: "", text: t("Veuillez sélectionner un rôle.") });
            return;
        }

        if (editingMenu && editingMenu.id) {
            updateMenu(editingMenu.id, formData);
        } else {
            createMenu(formData);
        }
        closeModal();
    };

    const onDeleteMenu = (menu) => {
        Swal.fire({
            title: t("Êtes-vous sûr ?"),
            text: t(`Voulez-vous vraiment supprimer le menu "${menu.name}" ?`),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e74c3c",
            cancelButtonColor: "#95a5a6",
            confirmButtonText: t("Oui, supprimer"),
            cancelButtonText: t("Annuler"),
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMenu(menu.id);
            }
        });
    };

    /** Company selection → store tenant → load roles (scoped) */
    const handleCompanyChange = (e) => {
        const id = e.target.value;
        setSelectedCompanyId(id);
        sessionStorage.setItem("selectedCompanyId", id);

        // optional store in redux
        const companyObj = (companies || []).find((c) => String(c.id) === String(id));
        if (companyObj) updateCompany(companyObj);

        // reset role + menus
        setSelectedRoleId("");
        setSelectedRole(null);

        if (id) {
            getAllRoles(); // tenant header filters roles by company
        }
    };

    /** Role selection → load menus by role */
    const handleRoleChange = (e) => {
        const rid = e.target.value;
        setSelectedRoleId(rid);

        const roleObj = (roles || []).find((r) => String(r.id) === String(rid)) || null;
        setSelectedRole(roleObj);

        if (rid) {
            getMenusByRole(selectedCompanyId,Number(rid));
        }
    };

    /** ---------- Drag & Drop (HTML5) ---------- */

    const onDragStart = (menu) => (ev) => {
        dragStateRef.current.draggedId = menu.id;
        dragStateRef.current.fromParentId = menu.parentMenuId ?? null;
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", String(menu.id));
    };

    const allowDrop = (ev) => {
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "move";
    };

    /**
     * Reorder within same parent or move to a new parent.
     * - targetParentId: null for root container, or parent menu id for child containers.
     * - targetIndex: where to insert (0..n-1). If null, append to end.
     */
    const handleDrop = ({ targetParentId, targetIndex = null }) => (ev) => {
        ev.preventDefault();
        const draggedIdStr = ev.dataTransfer.getData("text/plain");
        const draggedId = Number(draggedIdStr || dragStateRef.current.draggedId);

        if (!draggedId) return;

        const dragged = flatMenus.find((m) => m.id === draggedId);
        if (!dragged) return;

        const fromParentId = dragged.parentMenuId ?? null;

        // Build sibling lists
        const fromSiblings = (byParent.get(fromParentId) || []).slice();
        const toSiblings = (byParent.get(targetParentId) || []).slice();

        // Remove dragged from old siblings
        const fromIdx = fromSiblings.findIndex((m) => m.id === draggedId);
        if (fromIdx >= 0) fromSiblings.splice(fromIdx, 1);

        // If moving inside same parent, use updated fromSiblings as toSiblings
        let finalSiblings = toSiblings;
        let isSameParent = fromParentId === targetParentId;

        if (isSameParent) {
            finalSiblings = fromSiblings;
        } else {
            // ensure dragged removed from toSiblings if present (edge)
            const alreadyIdx = finalSiblings.findIndex((m) => m.id === draggedId);
            if (alreadyIdx >= 0) finalSiblings.splice(alreadyIdx, 1);
        }

        // Insert dragged in target
        const insertAt = targetIndex === null || targetIndex === undefined ? finalSiblings.length : Math.max(0, targetIndex);
        const movedItem = { ...dragged, parentMenuId: targetParentId ?? null };
        finalSiblings.splice(insertAt, 0, movedItem);

        // Now compute new orders for:
        // - fromSiblings (if different parent)
        // - finalSiblings (target)
        const updates = [];

        const pushUpdateIfChanged = (m, newParentId, newOrder) => {
            const parentIdNow = m.parentMenuId ?? null;
            const orderNow = m.displayOrder ?? null;

            const changedParent = parentIdNow !== (newParentId ?? null);
            const changedOrder = orderNow !== newOrder;

            if (changedParent || changedOrder) {
                updates.push({
                    id: m.id,
                    dto: toMenuRequestDTO({
                        ...m,
                        parentMenuId: newParentId ?? null,
                        displayOrder: newOrder,
                        // keep selected role if not present
                        roleId: m.roleId ?? (selectedRoleId ? Number(selectedRoleId) : null),
                    }),
                });
            }
        };

        // From parent reorder (only when moving to different parent)
        if (!isSameParent) {
            fromSiblings.forEach((m, idx) => pushUpdateIfChanged(m, fromParentId, idx + 1));
        }

        // Target parent reorder
        finalSiblings.forEach((m, idx) => pushUpdateIfChanged(m, targetParentId, idx + 1));

        if (updates.length === 0) return;

        // Dispatch updates sequentially
        updates.forEach((u) => updateMenu(u.id, u.dto));
    };

    /** Drop on a parent row → move dragged as last child of that parent */
    const dropOnParentRow = (parentMenuId) =>
        handleDrop({
            targetParentId: parentMenuId,
            targetIndex: null, // append
        });

    /** Drop on item row to reorder among siblings under same parent */
    const dropOnItemRow = (parentMenuId, index) =>
        handleDrop({
            targetParentId: parentMenuId,
            targetIndex: index,
        });

    const renderMenuRow = (menu, level, indexInParent, parentId) => {
        const isHidden = (menu.displayStatus || "").toUpperCase() === "HIDDEN";
        return (
            <div
                key={menu.id}
                className={`sbm-tree-row ${isHidden ? "sbm-tree-row-hidden" : ""}`}
                style={{ paddingLeft: `${12 + level * 18}px` }}
                draggable
                onDragStart={onDragStart(menu)}
                onDragOver={allowDrop}
                onDrop={dropOnItemRow(parentId ?? null, indexInParent)}
                title={t("Glisser-déposer pour réordonner")}
            >
                <div className="sbm-tree-left">
                    <span className="sbm-icon-badge">{menu.icon ? "🔹" : "📌"}</span>
                    <div className="sbm-tree-title">
                        <div className="sbm-tree-name">
                            <strong>{menu.name}</strong> <span className="sbm-tree-code">({menu.code})</span>
                        </div>
                        <div className="sbm-tree-sub">
              <span className={`sbm-status ${isHidden ? "sbm-status-hidden" : "sbm-status-visible"}`}>
                {isHidden ? "HIDDEN" : "VISIBLE"}
              </span>
                            {menu.routePath ? <span className="sbm-route">{menu.routePath}</span> : <span className="sbm-route sbm-route-empty">-</span>}
                        </div>
                    </div>
                </div>

                <div className="sbm-tree-actions">
                    <button className="sbm-btn-icon sbm-btn-addchild" onClick={() => openCreateModal(menu.id)} title={t("Ajouter sous-menu")}>
                        ➕
                    </button>
                    <button className="sbm-btn-icon sbm-btn-edit" onClick={() => openEditModal(menu)} title={t("Modifier")}>
                        ✏️
                    </button>
                    <button className="sbm-btn-icon sbm-btn-delete" onClick={() => onDeleteMenu(menu)} title={t("Supprimer")}>
                        🗑️
                    </button>
                </div>

                {/* Drop target: dropping ON this row can also mean "make it a child of this menu" (append) */}
                <div className="sbm-drop-parent-zone" onDragOver={allowDrop} onDrop={dropOnParentRow(menu.id)} title={t("Déposer ici pour déplacer comme sous-menu")}>
                    {t("Déposer pour en faire un sous-menu")}
                </div>
            </div>
        );
    };

    return (
        <div className="sbm-container">
            <div className="sbm-header">
                <h1>🧭 {t("Gestion des Menus (Sidebar)")}</h1>

                <div className="sbm-header-actions">
                    <button className="sbm-btn sbm-btn-add" onClick={() => openCreateModal(null)} disabled={!selectedCompanyId || !selectedRoleId}>
                        ➕ {t("Menu racine")}
                    </button>
                </div>
            </div>

            {/* Filters bar */}
            <div className="sbm-filters-bar">
                <div className="sbm-filter-group">
                    <label>{t("Entreprise")} *</label>
                    <select className="sbm-select" value={selectedCompanyId} onChange={handleCompanyChange}>
                        <option value="">{t("Sélectionner une entreprise...")}</option>
                        {(companies || []).map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name || c.code || `Company ${c.id}`}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="sbm-filter-group">
                    <label>{t("Rôle")} *</label>
                    <select className="sbm-select" value={selectedRoleId} onChange={handleRoleChange} disabled={!selectedCompanyId}>
                        <option value="">{selectedCompanyId ? t("Sélectionner un rôle...") : t("Choisir une entreprise d'abord")}</option>
                        {(roles || []).map((r) => (
                            <option key={r.id} value={r.id}>
                                {r.name || r.roleName || `Role ${r.id}`}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="sbm-filter-group sbm-search">
                    <label>{t("Recherche")}</label>
                    <div className="sbm-search-box">
                        <input
                            className="sbm-search-input"
                            type="text"
                            placeholder={t("Rechercher (code, nom, route)...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={!selectedRoleId}
                        />
                        <span className="sbm-search-icon">🔍</span>
                    </div>
                </div>

                <div className="sbm-filter-group">
                    <label>{t("Statut")}</label>
                    <select className="sbm-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} disabled={!selectedRoleId}>
                        <option value="ALL">{t("Tous")}</option>
                        <option value="VISIBLE">{t("VISIBLE")}</option>
                        <option value="HIDDEN">{t("HIDDEN")}</option>
                    </select>
                </div>
            </div>

            {/* Messages */}
            {error && <div className="sbm-alert sbm-alert-danger">{error}</div>}

            {/* Main content */}
            <div className="sbm-content">
                <div className="sbm-tree-panel">
                    <div className="sbm-panel-title">
                        <strong>🌳 {t("Arbre des menus")}</strong>
                        <span className="sbm-hint">{t("Drag & drop : déposer sur une ligne pour réordonner, ou déposer sur une zone parent pour changer de parent.")}</span>
                    </div>

                    {/* Root drop zone */}
                    <div
                        className="sbm-root-dropzone"
                        onDragOver={allowDrop}
                        onDrop={handleDrop({ targetParentId: null, targetIndex: null })}
                        title={t("Déposer ici pour déplacer en menu racine")}
                    >
                        {t("Zone racine (déposer ici pour rendre un menu racine)")}
                    </div>

                    <div className="sbm-tree-container">
                        {!selectedCompanyId ? (
                            <div className="sbm-empty">🏢 {t("Sélectionne une entreprise pour commencer.")}</div>
                        ) : !selectedRoleId ? (
                            <div className="sbm-empty">🧩 {t("Sélectionne un rôle pour afficher les menus.")}</div>
                        ) : loading ? (
                            <div className="sbm-loading">⏳ {t("Chargement...")}</div>
                        ) : filteredTree.length === 0 ? (
                            <div className="sbm-empty">😔 {t("Aucun menu trouvé pour ce rôle.")}</div>
                        ) : (
                            filteredTree.map(({ root, children }, rootIndex) => (
                                <div key={root.id} className="sbm-tree-block">
                                    {/* Root row */}
                                    {renderMenuRow(root, 0, rootIndex, null)}

                                    {/* Children drop container */}
                                    <div
                                        className="sbm-children-dropzone"
                                        onDragOver={allowDrop}
                                        onDrop={handleDrop({ targetParentId: root.id, targetIndex: null })}
                                        title={t("Déposer ici pour ajouter en sous-menu (fin de liste)")}
                                    >
                                        {children.length === 0 ? (
                                            <div className="sbm-children-empty">{t("Aucun sous-menu — déposer ici pour en ajouter")}</div>
                                        ) : (
                                            children.map((child, idx) => renderMenuRow(child, 1, idx, root.id))
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/*<div className="sbm-side-panel">*/}
                {/*    <div className="sbm-panel-title">*/}
                {/*        <strong>⚙️ {t("Actions rapides")}</strong>*/}
                {/*    </div>*/}

                {/*    <div className="sbm-card">*/}
                {/*        <div className="sbm-card-row">*/}
                {/*            <div className="sbm-card-label">{t("Entreprise sélectionnée")}:</div>*/}
                {/*            <div className="sbm-card-value">*/}
                {/*                {selectedCompanyId*/}
                {/*                    ? (companies || []).find((c) => String(c.id) === String(selectedCompanyId))?.name || `#${selectedCompanyId}`*/}
                {/*                    : "-"}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <div className="sbm-card-row">*/}
                {/*            <div className="sbm-card-label">{t("Rôle sélectionné")}:</div>*/}
                {/*            <div className="sbm-card-value">*/}
                {/*                {selectedRoleId*/}
                {/*                    ? (roles || []).find((r) => String(r.id) === String(selectedRoleId))?.name || `#${selectedRoleId}`*/}
                {/*                    : "-"}*/}
                {/*            </div>*/}
                {/*        </div>*/}

                {/*        <div className="sbm-card-actions">*/}
                {/*            <button className="sbm-btn sbm-btn-primary" onClick={() => openCreateModal(null)} disabled={!selectedCompanyId || !selectedRoleId}>*/}
                {/*                ➕ {t("Créer un menu racine")}*/}
                {/*            </button>*/}
                {/*        </div>*/}

                {/*        <div className="sbm-note">*/}
                {/*            <p>*/}
                {/*                <strong>{t("Note")}</strong> : {t("Le rôle dépend de l'entreprise (tenant). Assure-toi que le header X-Tenant-Id est envoyé via api.js.")}*/}
                {/*            </p>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</div>*/}
            </div>

            <SidebarMenuModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={onSubmitModal}
                editingMenu={editingMenu && editingMenu.id ? editingMenu : null}
                loading={loading}
                roles={roles}
                rootMenus={rootMenus}
                selectedRoleId={selectedRoleId}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    companies: state.companies?.companies || [], // <-- ajuste si ton combineReducers a un autre nom
    roles: state.role?.roles || [],
    menus: state.sidebarMenus?.adminMenus || [],
    loading: state.sidebarMenus?.loading,
    error: state.sidebarMenus?.error,
});

const mapDispatchToProps = (dispatch) => ({
    // companies
    getAllCompanies: () => dispatch(getAllCompaniesRequest()),
    updateCompany: (company) => dispatch(updateCompanyAction(company)),

    // roles
    getAllRoles: () => dispatch(getAllRolesRequest()),
    setSelectedRole: (role) => dispatch(setSelectedRoleAdmin(role)),

    // menus
    getMenusByRole: (selectedCompanyId,roleId) => dispatch(getSidebarMenusByRoleAdminRequest(selectedCompanyId,roleId)),

    createMenu: (data) => dispatch(createSidebarMenuRequest(data)),
    updateMenu: (menuId, data) => dispatch(updateSidebarMenuRequest(menuId, data)),
    deleteMenu: (menuId) => dispatch(deleteSidebarMenuRequest(menuId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SidebarMenuManagement);
